import React, { useState } from 'react';
import { useAuth } from '../components/AuthProvider';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { checkUsernameForPhishing } from '../lib/validation';

export const Login: React.FC = () => {
  const { login, user, supabaseUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [legacyFlowRequired, setLegacyFlowRequired] = useState(false);

  // States for user registration
  const [isRegistering, setIsRegistering] = useState(false);
  const [username, setUsername] = useState('');
  const [fullname, setFullname] = useState('');
  const [gender, setGender] = useState('Indefinido');
  const [birthDate, setBirthDate] = useState('2000-01-01');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsText, setTermsText] = useState('');

  const openTermsModal = async () => {
    setShowTermsModal(true);
    if (termsText) return;
    try {
      const res = await fetch('/termos.md');
      if (res.ok) {
        const text = await res.text();
        setTermsText(text);
      } else {
        setTermsText('Erro ao carregar o arquivo de Termos de Uso (/public/termos.md). Por favor, contacte o suporte ou tente mais tarde.');
      }
    } catch (err) {
      setTermsText('Erro de conexão ao carregar os termos de uso. Verifique sua conexão.');
    }
  };

  const ALLOWED_DOMAINS = [
    'gmail.com',
    'outlook.com',
    'outlook.com.br',
    'hotmail.com',
    'live.com',
    'yahoo.com',
    'icloud.com',
    'proton.me',
    'protonmail.com',
    'uol.com.br',
    'terra.com.br'
  ];

  const ALLOWED_GENDERS = ['Masculino', 'Feminino', 'Não binário', 'Indefinido', 'Outros'];

  const CURRENT_YEAR = new Date().getFullYear();
  const MAX_BIRTH_YEAR = CURRENT_YEAR - 10;

  React.useEffect(() => {
    if (!authLoading && supabaseUser) {
      navigate('/');
    }
  }, [supabaseUser, authLoading, navigate]);

  const getAge = (dateString: string): number => {
    if (!dateString) return 0;
    const today = new Date();
    const birth = new Date(dateString);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleVisitorLogin = async () => {
    setLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      // Use the demo account logic as requested for "Visitante"
      await login('demo@cardumy.dev', 'cardumy');
      navigate('/');
    } catch (err: any) {
      setError('Erro ao entrar como visitante: ' + (err.message || 'Falha na autenticação'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Por favor, preencha todos os campos.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccessMsg(null);

    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      console.error('Login error:', err);
      
      // Better error messaging for invalid credentials
      if (err.message?.includes('Invalid login credentials')) {
        setError('E-mail ou senha incorretos. Verifique seus dados.');
      } else {
        setError(err.message || 'Erro ao realizar login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    // 1. Limpeza básica de strings
    const cleanedEmail = email.trim();
    const emailParts = cleanedEmail.split('@');
    const domain = emailParts[emailParts.length - 1]?.toLowerCase();

    // 2. Validações Locais Invioláveis de Negócio
    if (!acceptedTerms) {
      setError('Você deve ler e aceitar os Termos de Uso e a Política de Privacidade para cadastrar-se.');
      return;
    }

    if (!cleanedEmail || !password) {
      setError('Por favor, preencha o E-mail e a Senha.');
      return;
    }

    if (!domain || !ALLOWED_DOMAINS.includes(domain)) {
      setError(`E-mail não permitido! Use um dos seguintes provedores: ${ALLOWED_DOMAINS.join(', ')}`);
      return;
    }

    if (!username.trim() || !fullname.trim()) {
      setError('Por favor, preencha o Nome de usuário e o Nome Completo.');
      return;
    }

    const usernameCheck = checkUsernameForPhishing(username);
    if (!usernameCheck.isValid) {
      setError(usernameCheck.error || 'Nome de usuário inválido.');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve conter no mínimo 6 caracteres.');
      return;
    }

    if (!birthDate) {
      setError('Por favor, preencha a sua Data de Nascimento.');
      return;
    }

    const today = new Date();
    const birth = new Date(birthDate);
    if (isNaN(birth.getTime())) {
      setError('A data de nascimento inserida é inválida.');
      return;
    }
    if (birth > today) {
      setError('A data de nascimento não pode estar inscrita no futuro.');
      return;
    }

    const age = getAge(birthDate);
    if (age < 10) {
      setError('A idade mínima para cadastro no Cardumy é de 10 anos.');
      return;
    }

    if (!ALLOWED_GENDERS.includes(gender)) {
      setError('Opção de gênero inválida.');
      return;
    }

    const processedUsername = username.toLowerCase().replace(/\s+/g, '');
    setLoading(true);

    try {
      // 3. Cadastra Usuário no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: cleanedEmail,
        password: password,
        options: {
          data: {
            username: processedUsername,
            fullname: fullname.trim(),
            gender: gender,
            birth_date: birthDate,
            phone: phone.trim() || null,
            bio: bio.trim() || 'Disponível para jogar!',
          }
        }
      });

      if (authError) throw authError;

      const user = authData?.user;
      if (!user) {
        throw new Error('Nenhum dado retornado do Supabase Auth. Verifique se o cadastro já existe.');
      }

      setSuccessMsg('Cadastro concluído com sucesso!');
      
      // Realiza o login imediato com a mesma senha para ativar a sessão no provedor de autenticação local de forma sincronizada
      await login(cleanedEmail, password);
      
      setTimeout(() => {
        navigate('/');
      }, 1000);

    } catch (err: any) {
      console.error('Erro no fluxo de autenticação:', err);
      // Se der erro de Unique Constraint por exemplo, exibe com clareza
      if (err.message?.includes('users_username_key') || err.message?.includes('duplicate key value violates unique constraint')) {
        setError('O Nome de Usuário informado já está em uso.');
      } else {
        setError(err.message || 'Erro inesperado ao realizar cadastro.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = () => {
    navigate('/reset-password');
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen py-10 px-4 sm:px-6 lg:px-8 bg-slate-950">
      <div className="max-w-md w-full space-y-6 bg-slate-900 p-6 sm:p-10 rounded-2xl sm:rounded-3xl border border-slate-800 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent opacity-40"></div>
        
        <div>
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center shadow-md shadow-purple-600/20 mb-6">
              <i className="fas fa-fish-fins text-3xl text-white"></i>
            </div>
          </div>
          <h2 className="text-center text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight">
            {isRegistering ? 'Criar sua conta no' : 'Seja bem-vindo ao'}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Cardumy</span>
          </h2>
          <p className="mt-2 text-center text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em]">
            {isRegistering ? 'Junte-se à maior comunidade TCG' : 'Sua jornada colecionável começa aqui'}
          </p>
        </div>

        {/* Toggle Abas - Entrar vs Cadastrar-se */}
        <div className="flex bg-slate-950/60 p-1 rounded-2xl border border-slate-800 mb-2">
          <button
            type="button"
            onClick={() => { setIsRegistering(false); setError(null); setSuccessMsg(null); }}
            className={`flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-colors duration-200 ${!isRegistering ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
          >
            Entrar
          </button>
          <button
            type="button"
            onClick={() => { setIsRegistering(true); setError(null); setSuccessMsg(null); }}
            className={`flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-colors duration-200 ${isRegistering ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
          >
            Cadastrar-se
          </button>
        </div>

        <form className="space-y-4" onSubmit={isRegistering ? handleSignUp : handleLogin}>
          {error && (
            <div className={`p-4 rounded-2xl text-[10px] font-bold border ${legacyFlowRequired ? 'bg-orange-500/10 border-orange-500/30 text-orange-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}>
              <div className="flex gap-3">
                <i className={`fas ${legacyFlowRequired ? 'fa-triangle-exclamation' : 'fa-circle-exclamation'} mt-0.5`}></i>
                <span>{error}</span>
              </div>
            </div>
          )}

          {successMsg && (
            <div className="p-4 rounded-2xl text-[10px] font-bold border bg-emerald-500/10 border-emerald-500/30 text-emerald-400">
              <div className="flex gap-3">
                <i className="fas fa-circle-check mt-0.5"></i>
                <span>{successMsg}</span>
              </div>
            </div>
          )}

          <div className="space-y-4">

            {isRegistering && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">Usuário</label>
                    <div className="relative group">
                      <i className="fas fa-at absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-purple-500 transition-colors"></i>
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                        className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl pl-12 pr-4 py-4 text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-colors outline-none"
                        placeholder="ex: red_tcg"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">Nome Completo</label>
                    <div className="relative group">
                      <i className="fas fa-user absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-purple-500 transition-colors"></i>
                      <input
                        type="text"
                        value={fullname}
                        onChange={(e) => setFullname(e.target.value)}
                        className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl pl-12 pr-4 py-4 text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-colors outline-none"
                        placeholder="ex: Red Ketchum"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Data de Nascimento - Modo Básico Nativo */}
                <div>
                  <div className="flex items-center justify-between mb-1.5 ml-1">
                    <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                      <span>Data de Nascimento</span>
                    </label>
                  </div>
                  <div className="relative group">
                    <i className="fas fa-calendar-day absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-purple-500 transition-colors pointer-events-none"></i>
                    <input
                      type="date"
                      max={`${MAX_BIRTH_YEAR}-12-31`}
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl pl-12 pr-4 py-4 text-sm text-white focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-colors outline-none [color-scheme:dark]"
                      disabled={loading}
                      required
                    />
                  </div>
                </div>

                {/* Gênero */}
                <div>
                  <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">Gênero</label>
                  <div className="relative group">
                    <i className="fas fa-venus-mars absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-purple-500 transition-colors"></i>
                    <select
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl pl-12 pr-10 py-4 text-base sm:text-sm text-white focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-colors outline-none appearance-none cursor-pointer"
                      disabled={loading}
                    >
                      {ALLOWED_GENDERS.map((g) => (
                        <option key={g} value={g} className="bg-slate-900 text-white">
                          {g}
                        </option>
                      ))}
                    </select>
                    <i className="fas fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-600 pointer-events-none text-[10px]"></i>
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">E-mail de Acesso</label>
              <div className="relative group">
                <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-purple-500 transition-colors"></i>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl pl-12 pr-4 py-4 text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-colors outline-none"
                  placeholder="ex: seuemail@gmail.com"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">Sua Senha</label>
              <div className="relative group">
                <i className="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-purple-500 transition-colors"></i>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl pl-12 pr-4 py-4 text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-colors outline-none"
                  placeholder="••••••••"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {isRegistering && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">WhatsApp (Opcional)</label>
                  <div className="relative group">
                    <i className="fas fa-phone absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-purple-500 transition-colors"></i>
                    <input
                      type="text"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl pl-12 pr-4 py-4 text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-colors outline-none"
                      placeholder="ex: 11999999999"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5 ml-1">Sua Bio (Opcional)</label>
                  <div className="relative group">
                    <i className="fas fa-comment-dots absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-purple-500 transition-colors"></i>
                    <input
                      type="text"
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl pl-12 pr-4 py-4 text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-colors outline-none"
                      placeholder="ex: Mestre Pokemon TCG"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {isRegistering && (
            <div className="flex items-start gap-3 mt-2 mb-4 bg-slate-950/40 p-3.5 rounded-2xl border border-slate-800">
              <input
                type="checkbox"
                id="accept-terms-checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 rounded border-slate-700 bg-slate-950 text-purple-600 focus:ring-purple-500 cursor-pointer h-4 w-4 shrink-0"
              />
              <label htmlFor="accept-terms-checkbox" className="text-[11px] text-slate-400 select-none leading-relaxed cursor-pointer">
                Declaro que li e concordo integralmente com os{' '}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); openTermsModal(); }}
                  className="text-purple-400 hover:text-purple-300 font-bold underline cursor-pointer inline-block"
                >
                  Termos de Uso
                </button>.
              </label>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-4 px-4 border border-transparent text-xs font-black uppercase tracking-[0.2em] rounded-2xl text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors shadow-md shadow-purple-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <i className="fas fa-circle-notch fa-spin text-lg animate-spin"></i>
              ) : isRegistering ? (
                'Criar Conta'
              ) : (
                'Entrar'
              )}
            </button>
          </div>
        </form>

        {!isRegistering && (
          <div className="text-center">
            <p className="text-[10px] text-slate-500 font-bold">
              Problemas com a senha? <span onClick={handleResetPassword} className="text-purple-400 hover:text-purple-300 font-black cursor-pointer transition-colors">Recuperar agora</span>
            </p>
          </div>
        )}
      </div>

      {/* Modal de Termos de Uso e Política de Privacidade */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
          <div className="bg-slate-900 border border-slate-800 max-w-2xl w-full max-h-[85vh] flex flex-col rounded-3xl overflow-hidden shadow-xl relative">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
              <h3 className="text-[11px] font-black text-white uppercase tracking-wider flex items-center gap-2">
                <i className="fas fa-file-signature text-purple-400"></i> Cardumy — Termos e Políticas da Plataforma
              </h3>
              <button 
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="text-slate-400 hover:text-white transition-colors p-2 text-xs"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 font-sans text-xs text-slate-300 leading-relaxed bg-slate-950/30 whitespace-pre-wrap select-text max-h-[55vh]">
              {termsText ? termsText : (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <i className="fas fa-circle-notch fa-spin text-purple-500 text-2xl animate-spin"></i>
                  <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">Carregando termos de uso...</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/60 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <span className="text-[9px] text-slate-500 font-mono italic">cardumyapp@gmail.com</span>
              <div className="flex gap-2 w-full sm:w-auto">
                {isRegistering && !acceptedTerms && (
                  <button 
                    type="button"
                    onClick={() => { setAcceptedTerms(true); setShowTermsModal(false); }}
                    className="flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white shadow-md transition-colors"
                  >
                    Aceitar e Fechar
                  </button>
                )}
                <button 
                  type="button"
                  onClick={() => setShowTermsModal(false)}
                  className="flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider bg-slate-800 hover:bg-slate-750 text-slate-300 border border-slate-700/50 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
